import { useEffect, useState } from "react";
import { Download, Trash2, CheckCircle2, AlertTriangle, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { LOCAL_MODELS, isCached, deleteCached } from "../../lib/localLlm";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";

export function ModelLoader({ modelId, setModelId, status, progress, onLoad, gpu, recommended }) {
  const [cached, setCached] = useState(false);
  const model = LOCAL_MODELS.find((m) => m.id === modelId);

  useEffect(() => {
    isCached(modelId).then(setCached);
  }, [modelId, status]);

  const remove = async () => {
    await deleteCached(modelId);
    setCached(false);
    toast.success("Modèle supprimé du navigateur");
  };

  if (gpu && !gpu.ok) {
    return (
      <div data-testid="local-ai-webgpu-error" className="flex gap-3 rounded-lg border border-amber-700/60 bg-amber-950/30 p-4 text-sm">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-400" />
        <div>
          <div className="font-medium text-amber-200">WebGPU indisponible sur ce navigateur</div>
          <p className="mt-1 text-xs text-amber-100/80">
            Ouvrez cette page avec Google Chrome ou Microsoft Edge à jour, sur ordinateur, avec l'accélération matérielle activée.
            En attendant, utilisez l'onglet « Questionnaire guidé », qui fonctionne partout.
          </p>
        </div>
      </div>
    );
  }

  const loading = status === "loading";
  return (
    <div className="space-y-3 rounded-lg border border-[#1E2638] bg-[#080B10] p-4" data-testid="local-ai-loader">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[220px] flex-1 space-y-1">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Modèle local</div>
          <Select value={modelId} onValueChange={setModelId} disabled={loading}>
            <SelectTrigger data-testid="local-ai-model-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LOCAL_MODELS.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name} · {m.size} · {m.id === recommended ? "Conseillé pour vous" : m.tier}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {status === "ready" ? (
          <span data-testid="local-ai-ready" className="flex h-10 items-center gap-1.5 text-sm text-emerald-400"><CheckCircle2 className="h-4 w-4" /> Prêt</span>
        ) : (
          <Button onClick={onLoad} disabled={loading || !gpu} data-testid="local-ai-load-button" className="h-10 gap-1.5">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {cached ? "Démarrer le modèle" : `Télécharger et démarrer (${model.size})`}
          </Button>
        )}
        {cached && !loading && (
          <Button variant="outline" onClick={remove} data-testid="local-ai-delete-button" className="h-10 gap-1.5 text-xs">
            <Trash2 className="h-3.5 w-3.5" /> Supprimer du navigateur
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground" data-testid="local-ai-model-note">
        {model.note}. {cached ? "Déjà téléchargé sur cet appareil." : "Téléchargement unique, conservé ensuite dans le navigateur."}
      </p>
      {recommended && (
        <p className="flex flex-wrap items-center gap-2 text-xs" data-testid="local-ai-recommendation">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          {recommended === modelId ? (
            <span className="text-primary">Modèle conseillé pour le matériel saisi dans le Diagnostic.</span>
          ) : (
            <>
              <span className="text-muted-foreground">Conseillé pour votre matériel : {LOCAL_MODELS.find((m) => m.id === recommended).name}</span>
              <button onClick={() => setModelId(recommended)} disabled={loading} data-testid="local-ai-use-recommended" className="text-primary hover:underline">Utiliser</button>
            </>
          )}
        </p>
      )}
      {loading && (
        <div className="space-y-1" data-testid="local-ai-progress">
          <Progress value={Math.round(progress.value * 100)} className="h-2" />
          <div className="font-mono2 text-[11px] text-muted-foreground">{Math.round(progress.value * 100)} % · {progress.text}</div>
        </div>
      )}
    </div>
  );
}
